import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import classNames from 'classnames';
import type { JSX, MouseEvent } from 'react';
import { LegacyTabs } from './LegacyTabs';
import { Tabs } from './Tabs';
import { type TabsProps, useTabs } from './useTabs';

export interface TabViewProps<TKey> extends TabsProps<TKey> {
  className?: string;
  contentClassName?: string;
  variant?: 'compact' | 'bookmark' | 'legacy';
  stretch?: boolean;
  showAddButton?: boolean;
  onAddTab?: () => void;
}

export const TabView = <TKey extends string = string>(
  props: TabViewProps<TKey>,
): JSX.Element => {
  const tabsHook = useTabs({
    activeTab: props.activeTab,
    tabs: props.tabs,
    onChangeTab: props.onChangeTab,
    initialActiveTab: props.initialActiveTab,
  });

  const handleAddTabClick = useStableCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();

      props.onAddTab?.();
    },
  );

  return (
    <div
      className={classNames(
        props.className,
        props.variant === 'legacy' && 'tab-view',
        'flex h-full flex-1 flex-col overflow-y-hidden pt-2.5',
      )}
    >
      {props.variant === 'legacy' ? (
        <LegacyTabs
          activeTab={tabsHook.activeTab}
          tabs={tabsHook.tabs}
          onChangeTab={tabsHook.handleChangeTab}
          stretch={props.stretch}
          showAddButton={props.showAddButton}
          onAddTabClick={handleAddTabClick}
        />
      ) : (
        <Tabs
          tabs={tabsHook.tabs}
          onChangeTab={tabsHook.handleChangeTab}
          selected={tabsHook.activeTab}
          variant={props.variant ?? 'bookmark'}
          showAddButton={props.showAddButton}
          onAddTabClick={handleAddTabClick}
        />
      )}
      <div
        className={classNames(
          'max-h-full flex-1',
          props.contentClassName,
          props.variant === 'legacy' ? 'tab-view__content' : 'bg-white',
        )}
      >
        {tabsHook.renderActiveTab()}
      </div>
    </div>
  );
};
